import { useEffect, useState } from "react";
import type { RefObject } from "react";

function countOverflowingChildren(container: HTMLDivElement): number {
  const { right: containerRight } = container.getBoundingClientRect();

  return Array.from(container.children).reduce(
    (count, child) => count + (child.getBoundingClientRect().left >= containerRight - 1 ? 1 : 0),
    0
  );
}

export function useOverflowCount(containerRef: RefObject<HTMLDivElement | null>) {
  const [overflowCount, setOverflowCount] = useState(0);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) return;

    const countHiddenChildren = () => setOverflowCount(countOverflowingChildren(container));

    const resizeObserver = new ResizeObserver(countHiddenChildren);
    resizeObserver.observe(container);

    // Re-measure when blob URLs resolve and image nodes appear/resize
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of Array.from(mutation.addedNodes)) {
          if (node instanceof Element) resizeObserver.observe(node);
        }
      }
      countHiddenChildren();
    });
    mutationObserver.observe(container, { childList: true, subtree: true });

    Array.from(container.children).forEach((child) => resizeObserver.observe(child));
    countHiddenChildren();

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [containerRef]);

  return overflowCount;
}
